import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { convertGoogleDriveUrl, getMediaType } from '@/lib/urlUtils';

const Lightbox = ({ project, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  // Combine thumbnail with media for navigation
  const allMedia = [
    { url: project.thumbnail, type: getMediaType(project.thumbnail) },
    ...(project.media || [])
  ];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  const next = () => {
    setCurrentIndex((prev) => (prev + 1) % allMedia.length);
  };

  const prev = () => {
    setCurrentIndex((prev) => (prev - 1 + allMedia.length) % allMedia.length);
  };

  const currentMedia = allMedia[currentIndex];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 lightbox-backdrop flex items-center justify-center"
        onClick={onClose}
        data-testid="lightbox"
      >
        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 text-white hover:bg-white/20 z-50"
          onClick={onClose}
          data-testid="lightbox-close"
        >
          <X className="h-6 w-6" />
        </Button>

        {/* Navigation Buttons */}
        {allMedia.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 z-50"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 z-50"
              onClick={(e) => { e.stopPropagation(); next(); }}
              data-testid="lightbox-next"
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          </>
        )}

        {/* Main Content */}
        <div 
          className="relative max-w-4xl max-h-[90vh] w-full mx-4 flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Media Display */}
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative flex-1 flex items-center justify-center"
          >
            {currentMedia?.type === 'video' ? (
              <div className="relative">
                <video
                  src={convertGoogleDriveUrl(currentMedia.url)}
                  className="max-h-[70vh] w-auto rounded-lg"
                  autoPlay={isPlaying}
                  loop
                  muted
                  playsInline
                  controls={false}
                  data-testid="lightbox-video"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute bottom-4 right-4 text-white bg-black/50 hover:bg-black/70"
                  onClick={() => setIsPlaying(!isPlaying)}
                  data-testid="lightbox-play-toggle"
                >
                  {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                </Button>
              </div>
            ) : (
              <img
                src={convertGoogleDriveUrl(currentMedia?.url)}
                alt={project.title}
                className="max-h-[70vh] w-auto rounded-lg object-contain"
                data-testid="lightbox-image"
              />
            )}
          </motion.div>

          {/* Project Info */}
          <div className="mt-6 text-center text-white">
            <h2 className="text-2xl font-serif mb-2" data-testid="lightbox-title">{project.title}</h2>
            <p className="text-white/80 text-sm max-w-xl mx-auto" data-testid="lightbox-description">
              {project.description}
            </p>
            
            {/* Media Counter */}
            {allMedia.length > 1 && (
              <div className="mt-4 flex justify-center gap-2">
                {allMedia.map((_, index) => (
                  <button
                    key={index}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentIndex ? 'bg-primary' : 'bg-white/30'
                    }`}
                    onClick={() => setCurrentIndex(index)}
                    data-testid={`lightbox-dot-${index}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Lightbox;
